import { isDev } from '@myre/common-conf';
import { CopierService } from '@myre/ui-primitive-core';
import { NotificationService } from '@myre/ui-primitive-notification';

import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  Input,
  output,
  signal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isNil } from 'lodash-es';

import { ClickOrLinkComponent } from '../click-or-link/click-or-link.component';

import { type ColorOrType } from './banner.directive';
import { mapDeprecatedColorToType, type BannerElement } from './helpers';

export type BannerTheme = 'info' | 'warn' | 'error';

/**
 * Banner to display list of messages for User Experience (can set messages and color)
 */
@Component({
  selector: 'myre-banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'status',
  },
  imports: [ClickOrLinkComponent],
})
export class BannerComponent {
  /**
   * Accepts either an object of type BannerElement or a straightforward string, serving as an equivalent to the BannerElement status but automatically translated
   */
  @Input()
  public set content(context: BannerElement | string) {
    if (typeof context === 'string') {
      context = { status: this._translateService.instant(context) };
    }

    this._content = context;
  }

  @Input()
  public type: BannerTheme = 'info';

  /**
   * @deprecated Use `content` instead
   * If you want to display multiple elements, use multiple `myre-banner` components (UX rule)
   */
  @Input()
  public set infoElements(elements: readonly BannerElement[]) {
    if (!elements?.length) {
      return;
    }

    if (elements.length > 1 && isDev()) {
      // eslint-disable-next-line no-console
      console.warn(
        'BannerComponent: some elements are ignored, only the first one is displayed. Deprecated usage.',
      );
    }

    this.content = elements[0];
  }

  /**
   * @deprecated Use `type` instead
   */
  @Input()
  public set color(color: ColorOrType) {
    this.type = mapDeprecatedColorToType(color);
  }

  public closable = input(false);

  public copyToClipboard = input(false);

  private _formatToClipboard: (content: BannerElement) => string = (
    content: BannerElement,
  ) => [content.status, ...(content.details ?? [])].join('\n');

  @Input()
  public set formatToClipboard(
    formatter: ((content: BannerElement) => string) | null | undefined,
  ) {
    if (!isNil(formatter)) {
      this._formatToClipboard = formatter;
    }
  }

  public get formatToClipboard(): (content: BannerElement) => string {
    return this._formatToClipboard;
  }

  public close = output<void>();

  protected readonly _hideBanner = signal(false);

  /**
   * @ignore
   */
  protected _content: BannerElement;

  private _counter = 0;

  private readonly _translateService = inject(TranslateService);
  private readonly _copierService = inject(CopierService);
  private readonly _notificationService = inject(NotificationService);

  public copy(): void {
    const textToCopy = this.formatToClipboard(this._content);

    this._copierService.copyText(textToCopy);

    this._notificationService.info({
      message: this._translateService.instant(
        '_library.clipboard.contentCopied',
      ),
      number: ++this._counter,
    });
  }

  protected _onClose(): void {
    this._hideBanner.set(true);
    this.close.emit();
  }
}
